import { useEffect, useState } from "react";
import { ListChecks, Download } from "lucide-react";
import api from "../lib/api";

/**
 * Admin-only horizontal bar breakdown of WHY users cancel — surfaces the
 * canonical reasons array from `/api/admin/retention/metrics` so the product
 * team can act on the top complaint without writing a SQL query.
 *
 * Sits next to RetentionSavesWidget on the admin Dashboard. Quietly hides
 * on auth failure or when zero cancellations exist (nothing to show).
 */
export default function CancelReasonsBreakdown() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/admin/retention/metrics")
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const downloadCSV = async (reasonId, label) => {
    setDownloading(reasonId);
    try {
      const res = await api.get(`/admin/retention/export?reason=${encodeURIComponent(reasonId)}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cancellations-${reasonId}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch {
      // ignore — admin link may have expired
    } finally {
      setDownloading(null);
    }
  };

  if (error || !data) return null;
  const reasons = data.reasons_breakdown || [];
  const total = data.reasons_total || 0;

  // Highest count drives the bar fill — when total=0 we still render the
  // empty-state nudge so the product team knows the panel exists.
  const max = reasons.reduce((m, r) => Math.max(m, r.count), 0) || 1;

  return (
    <div
      className="bg-white border border-slate-200 rounded-sm p-5"
      data-testid="cancel-reasons-breakdown"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-slate-700" />
          <h3 className="text-sm font-bold text-slate-900">Top cancel reasons · last 30 days</h3>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-slate-500" data-testid="cancel-reasons-total">
            {total} cancellation{total === 1 ? "" : "s"}
          </span>
          {total > 0 && (
            <button
              onClick={() => downloadCSV("all", "All reasons")}
              disabled={downloading === "all"}
              data-testid="cancel-reasons-export-all"
              className="text-[11px] text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 disabled:text-slate-400"
              title="Download all cancellations as CSV"
            >
              <Download className="w-3 h-3" />
              {downloading === "all" ? "…" : "Export"}
            </button>
          )}
        </div>
      </div>

      {total === 0 ? (
        <div className="text-center py-6 text-xs text-slate-500" data-testid="cancel-reasons-empty">
          No cancellations in the last 30 days. Nothing to fix yet.
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="cancel-reasons-list">
          {reasons.map((r, idx) => {
            const widthPct = (r.count / max) * 100;
            const isTop = idx === 0 && r.count > 0;
            return (
              <div
                key={r.id}
                className="flex items-center gap-3"
                data-testid={`cancel-reasons-row-${r.id}`}
              >
                <div className="text-xs text-slate-700 w-44 shrink-0 truncate" title={r.label}>
                  {isTop && <span className="text-rose-700 font-bold mr-1">●</span>}
                  {r.label}
                </div>
                <div className="flex-1 bg-slate-100 rounded-sm h-6 relative overflow-hidden">
                  <div
                    className={`h-full rounded-sm transition-all duration-500 ${
                      isTop
                        ? "bg-rose-600"
                        : r.count > 0
                          ? "bg-slate-700"
                          : "bg-slate-200"
                    }`}
                    style={{ width: r.count > 0 ? `${widthPct}%` : "2px" }}
                  />
                  {r.count > 0 && (
                    <div className="absolute inset-0 flex items-center justify-end pr-2">
                      <span className={`text-[11px] font-semibold ${widthPct > 35 ? "text-white" : "text-slate-700"}`}>
                        {r.count} · {r.pct}%
                      </span>
                    </div>
                  )}
                </div>
                {r.count > 0 ? (
                  <button
                    onClick={() => downloadCSV(r.id, r.label)}
                    disabled={downloading === r.id}
                    data-testid={`cancel-reasons-export-${r.id}`}
                    className="p-1 rounded text-slate-400 hover:text-slate-900 hover:bg-slate-100 disabled:text-slate-300 shrink-0"
                    title={`Download "${r.label}" feedback as CSV`}
                  >
                    {downloading === r.id ? (
                      <span className="text-[10px] font-bold">…</span>
                    ) : (
                      <Download className="w-3.5 h-3.5" />
                    )}
                  </button>
                ) : (
                  <span className="w-6 shrink-0" aria-hidden="true" />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
